import { useRef } from "react";

import CompositeColorPicker from "@/appComponents/Blocks/Composites/CompositeColorPicker/CompositeColorPicker";
import { appstr } from "@coreapp/utils/appstr";
import { BoxFlagSectionComposite } from "./BoxFlagSectionComposite";
import {
  BuildBorderDashOptions,
  BuildBorderStyleOptions,
  BuildBorderWeightOptions,
  BuildBoxShapeOptions,
  BuildLineStyleOptions
} from "@/appComponents/Navigation/ContextualToolbar/ToolbarOptions";
import { Checkbox, ColorTrigger, Flex, Menu, ToolIcon } from "@theorgchartdev/storybook";
import { encodeColorObject } from "@/appComponents/Blocks/Composites/CompositeColorPicker/ColorPicker.utils";
import { enumFillMode, kBoxOverlayRule } from "@coreapp/constants";
import { QChartTemplateRule } from "@coreapp/CoreApp/QChartTemplateRule";

/**
 * @typedef {object} BoxPropertiesSectionProps
 * @property {QChartTemplateRule} rule
 * @property {function} setRule
 * @property {string} actionType
 *
 * @param {BoxPropertiesSectionProps} props
 */
const BoxPropertiesSection = props => {
  const UpdateBoxFill = selectedColor => {
    const rule = props.rule.Clone();
    if (Array.isArray(selectedColor)) {
      rule.SetBoxFillMode(enumFillMode.linearGradientDown, props.actionType);
      rule.SetBoxFillColor(selectedColor[0], props.actionType);
      rule.SetBoxFillColorGradient(selectedColor[1], props.actionType);
    } else if (selectedColor) {
      rule.SetBoxFillMode(enumFillMode.solidFill, props.actionType);
      rule.SetBoxFillColor(selectedColor, props.actionType);
      rule.SetBoxFillColorGradient(null, props.actionType);
    } else {
      rule.SetBoxFillMode(enumFillMode.noFill, props.actionType);
    }
    props.setRule(rule);
  };

  const UpdateBoxBorderColor = selectedColor => {
    const rule = props.rule.Clone();
    rule.SetBoxBorderColor(selectedColor, props.actionType);
    props.setRule(rule);
  };

  const UpdateBoxShape = target => {
    const rule = props.rule.Clone();
    rule.SetBoxShapeType(target.value, props.actionType);
    props.setRule(rule);
  };

  const UpdateBorderWeight = target => {
    const rule = props.rule.Clone();
    rule.SetBorderThickness(target.value, props.actionType);
    props.setRule(rule);
  };

  const UpdateBorderStyle = target => {
    const rule = props.rule.Clone();
    if (
      target.label === "noborder" ||
      target.label === "singleborder" ||
      target.label === "dblborder"
    ) {
      rule.SetBorderStyle(target.value, props.actionType);
    } else {
      let dashedvalue = target.value - 3;
      rule.SetBorderDashStyle(dashedvalue, props.actionType);
    }
    props.setRule(rule);
  };

  const UpdateLineWeight = target => {
    const rule = props.rule.Clone();
    rule.SetLineWeight(target.value, props.actionType);
    props.setRule(rule);
  };

  const UpdateLineDash = target => {
    const rule = props.rule.Clone();
    rule.SetLineType(target.value, props.actionType);
    props.setRule(rule);
  };

  const UpdateLineColor = selectedLineColor => {
    const rule = props.rule.Clone();
    rule.SetLineColor(selectedLineColor, props.actionType);
    props.setRule(rule);
  };

  const handleToggleProperty = event => {
    const rule = props.rule.Clone();

    const settingToToggle = event.value;
    if (!rule.fActionSet[props.actionType]) {
      rule.fActionSet[props.actionType] = {};
    }
    const theSection = rule.fActionSet[props.actionType];
    theSection[settingToToggle] = !theSection[settingToToggle];

    props.setRule(rule);

    return theSection[settingToToggle];
  };

  const boxShapeOpenCtrlRef = useRef(null);
  const toggleBoxShapeCheckboxProps = {
    id: "toggleBoxProperties-shape",
    label: appstr("Box Shape"),
    size: "medium",
    checked: !!props.rule.GetShapeEnabled(props.actionType),
    onToggle: () => {
      const nextEnabled = handleToggleProperty({ value: "fShapeEnabled" });
      boxShapeOpenCtrlRef.current?.setOpen(nextEnabled);
    }
  };
  const boxShapeTrigger = (
    <ToolIcon
      disabled={!props.rule.GetShapeEnabled(props.actionType)}
      name={"shapes"}
      title={"Box shape"}
    />
  );
  const boxShapeOptions = BuildBoxShapeOptions(UpdateBoxShape, true);

  const boxFillOpenCtrlRef = useRef(null);
  const toggleBoxFillCheckboxProps = {
    id: "toggleBoxProperties-fill",
    label: appstr("Box Color"),
    size: "medium",
    checked: !!props.rule.GetFillEnabled(props.actionType),
    onToggle: () => {
      const nextEnabled = handleToggleProperty({ value: "fFillEnabled" });
      boxFillOpenCtrlRef.current?.setOpen(nextEnabled);
    }
  };

  const boxBorderWeightOpenCtrlRef = useRef(null);
  const boxBorderStyleOpenCtrlRef = useRef(null);
  const toggleBoxStyleCheckboxProps = {
    id: "toggleBoxProperties-weight",
    label: appstr("Border Style"),
    size: "medium",
    checked: !!props.rule.GetBorderEnabled(props.actionType),
    onToggle: () => {
      const nextEnabled = handleToggleProperty({ value: "fBorderEnabled" });
      boxBorderStyleOpenCtrlRef.current?.setOpen(nextEnabled);
      if (!nextEnabled) {
        boxBorderWeightOpenCtrlRef.current?.setOpen(false);
      }
    }
  };
  const borderWeightTrigger = (
    <ToolIcon
      disabled={!props.rule.GetBorderEnabled()}
      name={"lines-regular"}
      title={appstr("Border Weight")}
    />
  );
  const borderWeightOptions = BuildBorderWeightOptions(UpdateBorderWeight);

  const borderStyleTrigger = (
    <ToolIcon
      disabled={!props.rule.GetBorderEnabled(props.actionType)}
      name={"border-all"}
      title={appstr("Border type")}
    />
  );

  const borderstyle = BuildBorderStyleOptions(UpdateBorderStyle);
  const dashstyle = BuildBorderDashOptions(UpdateBorderStyle);
  borderstyle[borderstyle.length - 1].divider = true;
  let combinedstyles = borderstyle;
  for (var i = 0; i < dashstyle.length; i++) {
    dashstyle[i].value = dashstyle[i].value + 3;
    combinedstyles.push(dashstyle[i]);
  }
  const borderStyleOptions = combinedstyles;

  const borderColorOpenCtrlRef = useRef(null);
  const toggleBoxBorderColorCheckboxProps = {
    id: "toggleBoxProperties-bordercolor",
    label: appstr("Border Color"),
    size: "medium",
    checked: !!props.rule.GetBorderColorEnabled(props.actionType),
    onToggle: () => {
      const nextEnabled = handleToggleProperty({
        value: "fBorderColorEnabled"
      });
      borderColorOpenCtrlRef.current?.setOpen(nextEnabled);
    }
  };

  const boxFlagOpenCtrlRef = useRef(null);
  const toggleBoxFlagCheckBoxProps = {
    id: "toggleBoxProperties-triangleflag",
    label: appstr("Box Flag"),
    size: "medium",
    checked: !!props.rule.GetTriFlagEnabled(props.actionType),
    onToggle: () => {
      const nextEnabled = handleToggleProperty({
        value: "fTriFlagEnabled"
      });
      boxFlagOpenCtrlRef.current?.setOpen(nextEnabled);
    }
  };

  const boxLineColorOpenCtrlRef = useRef(null);
  const toggleBoxLineColorCheckboxProps = {
    id: "toggleBoxProperties-linecolor",
    label: appstr("Connecting Line Color"),
    size: "medium",
    checked: !!props.rule.GetLineColorEnabled(props.actionType),
    onToggle: () => {
      const nextEnabled = handleToggleProperty({
        value: "fLineColorEnabled"
      });
      boxLineColorOpenCtrlRef.current?.setOpen(nextEnabled);
    }
  };
  const boxLineDashOpenCtrlRef = useRef(null);
  const boxLineWeightOpenCtrlRef = useRef(null);
  const toggleBoxLineDashCheckboxProps = {
    id: "toggleBoxProperties-linedash",
    label: appstr("Connecting Line Style"),
    size: "medium",
    checked: !!props.rule.GetLineDashEnabled(props.actionType),
    onToggle: () => {
      const nextEnabled = handleToggleProperty({ value: "fLineDashEnabled" });
      boxLineDashOpenCtrlRef.current?.setOpen(nextEnabled);
      if (!nextEnabled) {
        boxLineWeightOpenCtrlRef.current?.setOpen(false);
      }
    }
  };
  const lineWeightTrigger = (
    <ToolIcon
      disabled={!props.rule.GetLineDashEnabled(props.actionType)}
      name={"lines-regular"}
      title={appstr("Line weight")}
    />
  );
  const lineWeightOptions = BuildBorderWeightOptions(UpdateLineWeight);

  const lineStyleTrigger = (
    <ToolIcon
      disabled={!props.rule.GetLineDashEnabled(props.actionType)}
      name={"border-top-left"}
      title={appstr("Line style")}
    />
  );

  const lineStyleOptions = BuildLineStyleOptions(UpdateLineDash);

  return (
    <>
      <span className={"view-rule-action-content-section-row"}>
        <Checkbox {...toggleBoxShapeCheckboxProps} />
        <Menu
          openCtrlRef={boxShapeOpenCtrlRef}
          optionSize={"small"}
          trigger={boxShapeTrigger}
          options={boxShapeOptions}
          value={props.rule.GetBoxShapeType(props.actionType)}
          disabled={!props.rule.GetShapeEnabled(props.actionType)}
        />
      </span>
      <span className={"view-rule-action-content-section-row"}>
        <Checkbox {...toggleBoxFillCheckboxProps} />
        <CompositeColorPicker
          enabled={!!props.rule.GetFillEnabled(props.actionType)}
          openCtrlRef={boxFillOpenCtrlRef}
          trigger={
            <ColorTrigger
              iconName="fill-drip"
              size={"medium"}
              disabled={!props.rule.GetFillEnabled(props.actionType)}
            />
          }
          showGradient
          onChange={UpdateBoxFill}
          currentColor={encodeColorObject(
            props.rule.GetBoxFillColor(props.actionType),
            props.rule.GetBoxFillColorGradient(props.actionType)
          )}
        />
      </span>
      <span className={"view-rule-action-content-section-row"}>
        <Checkbox {...toggleBoxStyleCheckboxProps} />
        <Flex gap="24px">
          <Menu
            openCtrlRef={boxBorderStyleOpenCtrlRef}
            trigger={borderStyleTrigger}
            options={borderStyleOptions}
            value={[
              props.rule.GetBorderStyle(props.actionType),
              props.rule.GetBorderDashStyle(props.actionType) + 3
            ]}
            disabled={!props.rule.GetBorderEnabled(props.actionType)}
            stayOpenOnOptionSelect
          />
          <Menu
            openCtrlRef={boxBorderWeightOpenCtrlRef}
            trigger={borderWeightTrigger}
            options={borderWeightOptions}
            value={props.rule.GetBorderThickness(props.actionType)}
            disabled={!props.rule.GetBorderEnabled(props.actionType)}
          />
        </Flex>
      </span>
      <span className={"view-rule-action-content-section-row"}>
        <Checkbox {...toggleBoxBorderColorCheckboxProps} />
        <CompositeColorPicker
          enabled={!!props.rule.GetBorderColorEnabled(props.actionType)}
          openCtrlRef={borderColorOpenCtrlRef}
          trigger={
            <ColorTrigger
              iconName="pen"
              size={"medium"}
              disabled={!props.rule.GetBorderColorEnabled(props.actionType)}
            />
          }
          onChange={UpdateBoxBorderColor}
          currentColor={props.rule.GetBoxBorderColor(props.actionType)}
        />
      </span>
      <span className={"view-rule-action-content-section-row"}>
        <Checkbox {...toggleBoxFlagCheckBoxProps} />
        <BoxFlagSectionComposite
          openCtrlRef={boxFlagOpenCtrlRef}
          disabled={!props.rule.GetTriFlagEnabled(props.actionType)}
          actionType={props.actionType}
          rule={props.rule}
          setRule={props.setRule}
        />
      </span>
      <span className={"view-rule-action-content-section-row"}>
        <Checkbox {...toggleBoxLineColorCheckboxProps} />
        <CompositeColorPicker
          enabled={!!props.rule.GetLineColorEnabled(props.actionType)}
          openCtrlRef={boxLineColorOpenCtrlRef}
          trigger={
            <ColorTrigger
              iconName="pen"
              size={"medium"}
              disabled={!props.rule.GetLineColorEnabled(props.actionType)}
            />
          }
          onChange={UpdateLineColor}
          currentColor={props.rule.GetLineColor(props.actionType)}
        />
      </span>
      <span className={"view-rule-action-content-section-row"}>
        <Checkbox {...toggleBoxLineDashCheckboxProps} />
        <Flex gap="24px">
          <Menu
            openCtrlRef={boxLineDashOpenCtrlRef}
            trigger={lineStyleTrigger}
            options={lineStyleOptions}
            value={props.rule.GetLineType(props.actionType)}
            disabled={!props.rule.GetLineDashEnabled(props.actionType)}
          />
          <Menu
            openCtrlRef={boxLineWeightOpenCtrlRef}
            trigger={lineWeightTrigger}
            options={lineWeightOptions}
            value={props.rule.GetLineWeight(props.actionType)}
            disabled={!props.rule.GetLineDashEnabled(props.actionType)}
          />
        </Flex>
      </span>
    </>
  );
};

export default BoxPropertiesSection;
